import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "expo-router";
import { useForm } from "react-hook-form";
import { ActivityIndicator, Alert, Text, View, StyleSheet, TouchableOpacity } from "react-native";
import * as z from "zod";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSupabase } from "@/hooks/useSupabase";
import { Button, Form, FormField, FormInput } from "@/components/ui";
import { P } from "@/components/ui/typography";
import { trpc } from "@/trpc/react";
import { KeyboardAwareScrollView, KeyboardToolbar } from "react-native-keyboard-controller";
import colors from "@/constants/Colors";

const formSchema = z
  .object({
    email: z.string().email("Please enter a valid email address."),
    password: z
      .string()
      .min(8, "Please enter at least 8 characters.")
      .max(64, "Please enter fewer than 64 characters.")
      .regex(
        /^(?=.*[a-z])/,
        "Your password must have at least one lowercase letter.",
      )
      .regex(
        /^(?=.*[A-Z])/,
        "Your password must have at least one uppercase letter.",
      ),
    confirmPassword: z.string().min(8, "Please enter at least 8 characters."),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Your passwords do not match.",
    path: ["confirmPassword"],
  });

export default function SignUp() {
  const { signUp } = useSupabase();
  const router = useRouter();
  const insets = useSafeAreaInsets();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { mutate: createRecruitment } = trpc.recruitment.create.useMutation({
    onSuccess(_response) {
    },
  });

  const { mutate: createCrew } = trpc.crew.createCrew.useMutation({
    onSuccess(response, variables) {
      createRecruitment({
        email: variables.email,
        user_id: variables.user_id,
        crew_id: response.id
      });
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    try {
      const { session } = await signUp(data.email, data.password);

      form.reset();

      if (!session || !session?.user.id) {
        Alert.alert('Please check your inbox for email verification!')
        return;
      }

      createCrew({
        email: data.email,
        user_id: session.user.id
      });

    } catch (error: Error | any) {
      Alert.alert(error.message)
    }
  }

  return (
    <>
      <KeyboardAwareScrollView
        bottomOffset={62}
        style={styles.container}
        contentContainerStyle={{
          flexGrow: 1,
          paddingTop: 40 + insets.top,
          paddingBottom: 20 + insets.bottom,
          paddingHorizontal: 20,
        }}
      >
        <View style={styles.header}>
          <Text style={styles.title}>Become a Crewsader</Text>
          <Text style={styles.subtitle}>Let's create your account</Text>
        </View>

        <View style={styles.infoSection}>
          <Text style={styles.infoText}>
            You are about to apply to become a Crewsader. Due to a high number of applications we will only be able to respond to successful candidates and will endeavour to do so within 5 working days.
          </Text>
        </View>

        <View style={styles.formContainer}>
          <Form {...form}>
            <View style={styles.inputsContainer}>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormInput
                    label="Email"
                    placeholder="Enter your email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect={false}
                    keyboardType="email-address"
                    textContentType="username"
                    className="border-gray-300 bg-white"
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormInput
                    label="Password"
                    placeholder="Create password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="new-password"
                    textContentType="newPassword"
                    passwordRules="minlength: 8; required: lower; required: upper;"
                    secureTextEntry
                    className="border-gray-300 bg-white"
                    {...field}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormInput
                    label="Confirm Password"
                    placeholder="Confirm password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    autoComplete="new-password"
                    textContentType="newPassword"
                    secureTextEntry
                    className="border-gray-300 bg-white"
                    {...field}
                  />
                )}
              />
            </View>
          </Form>
        </View>

        <View style={styles.actionsContainer}>
          <Button
            size="default"
            variant="crew"
            style={styles.signUpButton}
            onPress={form.handleSubmit(onSubmit)}
          >
            {form.formState.isSubmitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              "Continue"
            )}
          </Button>

          <View style={styles.signInContainer}>
            <Text style={styles.signInText}>
              Already have an account?{" "}
            </Text>
            <TouchableOpacity
              onPress={() => {
                router.replace("/sign-in");
              }}
            >
              <Text style={styles.signInLink}>Sign in</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAwareScrollView>

      <KeyboardToolbar />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f7',
  },
  header: {
    marginBottom: 20,
    alignItems: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#000',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  infoSection: {
    marginBottom: 20,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#eaeaea',
  },
  infoText: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
  },
  formContainer: {
    backgroundColor: 'transparent',
    borderRadius: 16,
    width: '100%',
    marginBottom: 20,
  },
  inputsContainer: {
    gap: 16,
  },
  actionsContainer: {
    width: '100%',
    marginTop: 'auto',
  },
  signUpButton: {
    marginVertical: 16,
    borderRadius: 12,
    height: 54,
  },
  signInContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  signInText: {
    color: '#666',
    fontSize: 15,
  },
  signInLink: {
    color: colors.primary,
    fontSize: 15,
    fontWeight: 'bold',
  },
});